import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { getPowerBiConfigStatus, fetchPowerBiEmbedToken, registerPowerBiReport, PowerBiEmbedConfig } from '../../services/powerBiService';
import { GlobalUser, Task, PhysicalUnit, WeeklyPlan, Contract, PaymentStatement, ProjectProgressSnapshot } from '../../types';
import { useSyncState } from '../../hooks/useSyncState';
import { calculateProjectProgress, calculateSCurve, truncateProgress } from '../../utils/planningUtils';
import * as pbi from 'powerbi-client';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell
} from 'recharts';

interface PowerBIEmbedContainerProps {
    reportId?: string;
    currentUser?: GlobalUser;
    initialMode?: 'view' | 'edit';
    canView?: boolean;
    canDesign?: boolean;
    canModifyDesign?: boolean;
    context?: 'consumption' | 'designer';
    action?: 'view' | 'edit' | 'create';
    onReportSaved?: (powerbiReportId: string, name: string) => void;
    datasetId?: string;
}

export const PowerBIEmbedContainer: React.FC<PowerBIEmbedContainerProps> = ({
    reportId,
    currentUser,
    initialMode = 'view',
    canView = false,
    canDesign = false,
    canModifyDesign = false,
    context = 'consumption',
    action = 'view',
    onReportSaved,
    datasetId
}) => {
    const { instance, accounts } = useMsal();
    const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<{ code?: string; message: string; requestId?: string } | null>(null);
    const [config, setConfig] = useState<Partial<PowerBiEmbedConfig> & { isMocked?: boolean; name?: string; metadata?: any }>({});
    const [status] = useState(getPowerBiConfigStatus());
    const [showDiagPanel, setShowDiagPanel] = useState<boolean>(false);
    const embedContainerRef = useRef<HTMLDivElement>(null);
    const refreshTimerRef = useRef<any>(null);
    const reportRef = useRef<any>(null);

    // --- CARGA DE DATOS LOCALES PARA EL MOTOR INTERNO ---
    const activeProjectId = typeof window !== 'undefined' ? (localStorage.getItem('selectedProjectId') || 'data') : 'data';
    const collectionName = activeProjectId !== 'data' ? `project_${activeProjectId}` : 'data';

    const [localProjectInfo] = useSyncState<any>(collectionName, 'projectInfo', {
        name: 'Obra Activa',
        startDate: '2026-01-05',
        endDate: '2026-12-31'
    });
    const [localTasks] = useSyncState<Task[]>(collectionName, 'tasks', []);
    const [localUnits] = useSyncState<PhysicalUnit[]>(collectionName, 'units', []);
    const [localMatrixData] = useSyncState<Record<string, any>>(collectionName, 'matrixData', {});
    const [localProtocolConfigs] = useSyncState<Record<string, any>>(collectionName, 'protocolConfigs', {});
    const [localSavedProtocols] = useSyncState<Record<string, any>>(collectionName, 'savedProtocols', {});
    const [localWeeklyPlans] = useSyncState<WeeklyPlan[]>(collectionName, 'weeklyPlans', []);
    const [localContracts] = useSyncState<Contract[]>(collectionName, 'contracts', []);
    const [localPaymentStatements] = useSyncState<PaymentStatement[]>(collectionName, 'paymentStatements', []);
    const [localProgressHistory] = useSyncState<ProjectProgressSnapshot[]>(collectionName, 'progressHistory', []);
    const [incidences] = useSyncState<any[]>(collectionName, 'incidences', []);

    // --- ESTADOS DEL DISEÑADOR LOCAL MOCK ---
    const [reportTitle, setReportTitle] = useState<string>('');
    const [reportScope, setReportScope] = useState<'obra' | 'multiobra' | 'corporativo'>('obra');
    const [reportCategory, setReportCategory] = useState<string>('Planificación');
    const [reportDescription, setReportDescription] = useState<string>('');
    const [selectedComponents, setSelectedComponents] = useState<string[]>(['kpi', 'scurve', 'ppc', 'quality', 'subcontracts']);
    const [savingLocal, setSavingLocal] = useState<boolean>(false);

    // --- ESTADOS DEL VISOR LOCAL MOCK ---
    const [localActiveTab, setLocalActiveTab] = useState<string>('resumen');

    // Inicializar el diseñador con datos si es modo edición/creación
    useEffect(() => {
        if (config.name) {
            setReportTitle(config.name);
        }
        if (config.metadata?.category) {
            setReportCategory(config.metadata.category);
        }
        if (config.metadata?.description) {
            setReportDescription(config.metadata.description);
        }
        if (config.metadata?.components) {
            setSelectedComponents(config.metadata.components);
        }
    }, [config]);

    const getFreshMsalToken = async () => {
        const account = instance.getActiveAccount() || accounts[0];
        if (account) {
            try {
                const response = await instance.acquireTokenSilent({
                    scopes: ["User.Read"],
                    account: account
                });
                localStorage.setItem('klyd_msal_access_token', response.accessToken);
                return response.accessToken;
            } catch (e) {
                console.warn("Silent token acquisition failed:", e);
            }
        }
        return null;
    };

    const isAdmin = currentUser?.globalRole === 'Administrador' || currentUser?.globalRole === ('SYSADMIN' as any);

    const scheduleTokenRefresh = (expirationTimeStr?: string) => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }

        if (!expirationTimeStr || config.isMocked) return;

        const expirationDate = new Date(expirationTimeStr);
        const now = new Date();
        const timeToExpiration = expirationDate.getTime() - now.getTime();

        const leadTime = 5 * 60 * 1000;
        let refreshIn = timeToExpiration - leadTime;
        if (refreshIn < 0) {
            refreshIn = Math.max(30 * 1000, timeToExpiration - 10 * 1000);
        }

        console.log(`Scheduling token refresh in ${(refreshIn / 1000).toFixed(0)} seconds.`);

        refreshTimerRef.current = setTimeout(() => {
            console.log("Triggering scheduled Embed Token refresh...");
            refreshToken();
        }, refreshIn);
    };

    const refreshToken = async () => {
        if (config.isMocked) return;
        try {
            await getFreshMsalToken();
            const effectiveMode = (mode === 'edit' && !isAdmin && !canModifyDesign) ? 'view' : mode;
            const res = await fetchPowerBiEmbedToken(reportId, effectiveMode, context, action, datasetId);
            setConfig(res);

            if (reportRef.current && res.accessToken) {
                await reportRef.current.setAccessToken(res.accessToken);
                console.log("Embed Token dynamically updated in active session.");
            }

            scheduleTokenRefresh(res.expiresAt);
        } catch (err: any) {
            console.error("Failed to refresh Power BI token, retrying in 30 seconds:", err);
            refreshTimerRef.current = setTimeout(refreshToken, 30 * 1000);
        }
    };

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        const effectiveMode = (mode === 'edit' && !isAdmin && !canModifyDesign) ? 'view' : mode;
        getFreshMsalToken().then(() => {
            fetchPowerBiEmbedToken(reportId, effectiveMode, context, action, datasetId)
                .then((res) => {
                    if (isMounted) {
                        setConfig(res);
                        scheduleTokenRefresh(res.expiresAt);
                        setLoading(false);
                    }
                })
                .catch((err: any) => {
                    console.error("Error fetching Power BI embed config:", err);
                    if (isMounted) {
                        setError({
                            code: err.code || 'CONNECTION_ERROR',
                            message: err.message || 'Error al establecer la conexión segura con el servidor analítico.',
                            requestId: err.requestId
                        });
                        setLoading(false);
                    }
                });
        });

        return () => {
            isMounted = false;
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
        };
    }, [reportId, mode, action, datasetId]);

    // SDK Embed logic for active production tokens
    useEffect(() => {
        if (!embedContainerRef.current || !config.accessToken || config.isMocked) {
            return;
        }

        // Initialize the Power BI client service
        const powerbi = new pbi.service.Service(
            (pbi.factories as any).hpm,
            (pbi.factories as any).wpmp,
            (pbi.factories as any).router
        );

        let report: any;

        if (action === 'create') {
            const embedConfig = {
                type: 'report',
                tokenType: pbi.models.TokenType.Embed,
                accessToken: config.accessToken,
                embedUrl: config.embedUrl,
                datasetId: config.datasetId,
                settings: {
                    panes: {
                        filters: { visible: true, expanded: false },
                        fields: { visible: true, expanded: true }
                    }
                }
            };
            report = powerbi.createReport(embedContainerRef.current, embedConfig as any);
        } else {
            if (!config.embedUrl || !config.reportId) return;

            const embedConfig = {
                type: 'report',
                tokenType: pbi.models.TokenType.Embed,
                accessToken: config.accessToken,
                embedUrl: config.embedUrl,
                id: config.reportId,
                permissions: mode === 'edit' ? pbi.models.Permissions.All : pbi.models.Permissions.Read,
                viewMode: mode === 'edit' ? pbi.models.ViewMode.Edit : pbi.models.ViewMode.View,
                settings: {
                    panes: {
                        filters: {
                            visible: true,
                            expanded: false
                        },
                        pageNavigation: {
                            visible: true
                        }
                    }
                }
            };
            report = powerbi.embed(embedContainerRef.current, embedConfig as any);
        }

        reportRef.current = report;

        // Listen for save events in creation or edit mode
        report.on("saved", (event: any) => {
            const { reportId: savedReportId, reportName } = event.detail;
            console.log("Report saved event triggered:", savedReportId, reportName);
            if (onReportSaved) {
                onReportSaved(savedReportId, reportName);
            }
        });

        return () => {
            if (embedContainerRef.current) {
                powerbi.reset(embedContainerRef.current);
            }
            reportRef.current = null;
        };
    }, [config, mode, action]);

    // --- CÁLCULOS ANALÍTICOS LOCALES ---
    const formatCLP = (val: number) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(val);
    };

    const projectProgress = useMemo(() => {
        if (!localTasks || localTasks.length === 0) return 0;
        try {
            return calculateProjectProgress(
                localTasks,
                localUnits,
                localMatrixData,
                localProtocolConfigs,
                localProjectInfo,
                localSavedProtocols,
                localWeeklyPlans
            );
        } catch (e) {
            console.error(e);
            return 0;
        }
    }, [localTasks, localUnits, localMatrixData, localProtocolConfigs, localProjectInfo, localSavedProtocols, localWeeklyPlans]);

    const ppcMetrics = useMemo(() => {
        let executed = 0;
        let notExecuted = 0;
        let missing = 0;

        localWeeklyPlans.forEach((plan: any) => {
            if (plan.status === 'Cerrada' && Array.isArray(plan.allocations)) {
                plan.allocations.forEach((alloc: any) => {
                    if (alloc.isWeeklySubtask === true) return; // Regla de negocio de AGENTS.md
                    if (alloc.executed === true) {
                        executed++;
                    } else if (alloc.executed === false) {
                        notExecuted++;
                    } else {
                        missing++;
                    }
                });
            }
        });

        const totalValid = executed + notExecuted;
        const ppcValid = totalValid > 0 ? (executed / totalValid) * 100 : 0;
        const totalGross = executed + notExecuted + missing;
        const ppcGross = totalGross > 0 ? (executed / totalGross) * 100 : 0;

        return {
            ppcValid,
            ppcGross,
            executed,
            notExecuted,
            missing,
            totalValid
        };
    }, [localWeeklyPlans]);

    const qualityMetrics = useMemo(() => {
        let openCount = 0;
        let closedCount = 0;
        let rejectedCount = 0;

        Object.values(localSavedProtocols).forEach((p: any) => {
            if (p) {
                const s = (p.status || '').toLowerCase();
                if (s.includes('cerr') || s === 'closed' || s === 'approved') {
                    closedCount++;
                } else if (s.includes('rech') || s === 'rejected' || s === 'failed') {
                    rejectedCount++;
                } else {
                    openCount++;
                }
            }
        });

        const total = openCount + closedCount + rejectedCount;
        return { total, openCount, closedCount, rejectedCount };
    }, [localSavedProtocols]);

    const financialMetrics = useMemo(() => {
        let contracted = 0;
        let approved = 0;
        let retentions = 0;

        localContracts.forEach((c: any) => {
            if (c && c.status !== 'Anulado') {
                contracted += (c.amount || 0);
            }
        });

        localPaymentStatements.forEach((ps: any) => {
            if (ps && ps.status === 'Aprobado') {
                approved += (ps.netAmount || 0);
                retentions += (ps.retentionAmount || (ps.netAmount * 0.05) || 0);
            }
        });

        return { contracted, approved, retentions };
    }, [localContracts, localPaymentStatements]);

    const scurvePoints = useMemo(() => {
        if (!localTasks || localTasks.length === 0) return [];
        try {
            return calculateSCurve(localTasks, localProgressHistory);
        } catch (e) {
            console.error(e);
            return [];
        }
    }, [localTasks, localProgressHistory]);

    // PPC por Semana
    const ppcTrendData = useMemo(() => {
        const sortedPlans = [...localWeeklyPlans]
            .filter(p => p.status === 'Cerrada' && p.weekRange)
            .sort((a, b) => (a.weekRange || '').localeCompare(b.weekRange || ''));

        return sortedPlans.map((p, idx) => {
            let executed = 0;
            let total = 0;
            p.allocations?.forEach((alloc: any) => {
                if (alloc.isWeeklySubtask === true) return;
                if (alloc.executed === true) {
                    executed++;
                    total++;
                } else if (alloc.executed === false) {
                    total++;
                }
            });
            const pct = total > 0 ? (executed / total) * 100 : 0;
            return {
                name: `Sem ${idx + 1}`,
                rango: p.weekRange,
                PPC: Number(pct.toFixed(1))
            };
        });
    }, [localWeeklyPlans]);

    // Datos Financieros Subcontratos
    const subcontractChartData = useMemo(() => {
        return localContracts.map(c => {
            const approvedAmount = localPaymentStatements
                .filter(ps => ps.contractId === c.id && ps.status === 'Aprobado')
                .reduce((sum, ps) => sum + (ps.netAmount || 0), 0);
            return {
                name: c.subcontractorName?.substring(0, 15) || 'Subcontratista',
                Contratado: c.amount || 0,
                Visado: approvedAmount
            };
        }).slice(0, 8); // Top 8 subcontratos
    }, [localContracts, localPaymentStatements]);

    const isEditAuthorized = config.permissions === 'ReadWrite' || config.isMocked;
    const showPermissionError = mode === 'edit' && !isEditAuthorized && !canDesign;
    const hasWarningBanner = !isAdmin && !canModifyDesign && mode === 'edit' && canDesign && !config.isMocked;

    // --- ACCIÓN DE EXPORTACIÓN ---
    const handleExportPDF = () => {
        window.print();
    };

    const handleExportHTML = () => {
        const htmlContent = `<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <title>Reporte: ${config.name || 'Reporte de Gestión'}</title>
    <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 40px; color: #1e293b; background-color: #f8fafc; }
        .container { max-width: 1000px; margin: 0 auto; background: white; padding: 40px; border-radius: 16px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); }
        h1 { color: #136dec; border-bottom: 2px solid #e2e8f0; padding-bottom: 12px; font-size: 24px; display: flex; align-items: center; gap: 8px; }
        .meta { color: #64748b; font-size: 13px; margin-bottom: 24px; }
        .kpi-container { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 32px; }
        .kpi-card { border: 1px solid #e2e8f0; padding: 20px; border-radius: 12px; background: #f8fafc; }
        .kpi-title { font-size: 11px; text-transform: uppercase; font-weight: bold; color: #64748b; margin-bottom: 6px; }
        .kpi-val { font-size: 22px; font-weight: 900; color: #136dec; }
        .chart-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 32px; margin-bottom: 32px; }
        .chart-card { border: 1px solid #e2e8f0; padding: 24px; border-radius: 16px; background: white; }
        .chart-title { font-size: 12px; font-weight: bold; color: #334155; margin-bottom: 16px; text-transform: uppercase; }
        table { width: 100%; border-collapse: collapse; margin-top: 24px; }
        th, td { border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-size: 13px; }
        th { background-color: #f1f5f9; font-weight: bold; color: #475569; }
        tr:hover { background-color: #f8fafc; }
    </style>
</head>
<body>
    <div class="container">
        <h1>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align: middle;">
                <rect x="3" y="3" width="18" height="18" rx="2" fill="#136dec"/>
                <path d="M7 16V14H9V16H7ZM11 16V10H13V16H11ZM15 16V12H17V16H15Z" fill="white"/>
            </svg>
            Reporte de Gestión de Obra: ${config.name || 'Reporte Local'}
        </h1>
        <div class="meta">Generado el: ${new Date().toLocaleString('es-CL')} | K-Lyapp ConstructOS</div>
        
        <div class="kpi-container">
            <div class="kpi-card">
                <div class="kpi-title">Avance Físico Global</div>
                <div class="kpi-val">${projectProgress.toFixed(2)}%</div>
            </div>
            <div class="kpi-card">
                <div class="kpi-title">PPC Válido (LPS)</div>
                <div class="kpi-val">${ppcMetrics.ppcValid.toFixed(1)}%</div>
            </div>
            <div class="kpi-card">
                <div class="kpi-title">Pagos Subcontratos</div>
                <div class="kpi-val">${formatCLP(financialMetrics.approved)}</div>
            </div>
            <div class="kpi-card">
                <div class="kpi-title">Garantía Retenida (5%)</div>
                <div class="kpi-val">${formatCLP(financialMetrics.retentions)}</div>
            </div>
        </div>

        <div class="chart-grid">
            <div class="chart-card">
                <div class="chart-title">Curva S de Avance de Obra</div>
                <div style="height: 260px; position: relative;">
                    <canvas id="scurveChart"></canvas>
                </div>
            </div>
            <div class="chart-card">
                <div class="chart-title">Cumplimiento de Planificación (PPC)</div>
                <div style="height: 260px; position: relative;">
                    <canvas id="ppcChart"></canvas>
                </div>
            </div>
        </div>

        <h2>Detalle de Tareas del Proyecto</h2>
        <table>
            <thead>
                <tr>
                    <th>Nombre de la Tarea</th>
                    <th>Inicio</th>
                    <th>Fin</th>
                    <th>Duración (Días)</th>
                    <th>Avance (%)</th>
                </tr>
            </thead>
            <tbody>
                ${(localTasks || []).map((t: any) => `
                    <tr>
                        <td><strong>${t.name}</strong></td>
                        <td>${t.start || '-'}</td>
                        <td>${t.end || '-'}</td>
                        <td>${t.duration || 0}</td>
                        <td>${(t.progress || 0).toFixed(1)}%</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    </div>

    <script>
        const scurveData = ${JSON.stringify(scurvePoints)};
        const ppcData = ${JSON.stringify(ppcTrendData)};

        const ctxS = document.getElementById('scurveChart').getContext('2d');
        new Chart(ctxS, {
            type: 'line',
            data: {
                labels: scurveData.map(d => d.name),
                datasets: [
                    {
                        label: 'Progreso Programado',
                        data: scurveData.map(d => d.plan),
                        borderColor: '#3b82f6',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        fill: true,
                        tension: 0.3,
                        borderWidth: 2
                    },
                    {
                        label: 'Progreso Real',
                        data: scurveData.map(d => d.real),
                        borderColor: '#10b981',
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        fill: true,
                        tension: 0.3,
                        borderWidth: 2
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: { min: 0, max: 100 }
                }
            }
        });

        const ctxP = document.getElementById('ppcChart').getContext('2d');
        new Chart(ctxP, {
            type: 'bar',
            data: {
                labels: ppcData.map(d => d.name),
                datasets: [{
                    label: 'Porcentaje PPC',
                    data: ppcData.map(d => d.PPC),
                    backgroundColor: ppcData.map(d => d.PPC >= 85 ? '#10b981' : '#f59e0b'),
                    borderRadius: 4
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: { min: 0, max: 100 }
                }
            }
        });
    </script>
</body>
</html>`;
        const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Reporte_Obra_${config.name || 'Local'}.html`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportCSV = () => {
        let csvContent = "\uFEFF"; // BOM para asegurar caracteres UTF-8 en Excel
        csvContent += "Módulo,Elemento,Métrica,Valor\n";
        csvContent += `Planificación,Avance Físico Global,Porcentaje,${projectProgress.toFixed(2)}%\n`;
        csvContent += `Planificación,Cumplimiento PPC Válido,Porcentaje,${ppcMetrics.ppcValid.toFixed(2)}%\n`;
        csvContent += `Subcontratos,Total Contratado,Monto Neto,${financialMetrics.contracted}\n`;
        csvContent += `Subcontratos,Total Pagado Visado,Monto Neto,${financialMetrics.approved}\n`;
        csvContent += `Subcontratos,Total Garantía Retenida,Monto Neto,${financialMetrics.retentions}\n`;
        
        csvContent += "\nID,Tarea,Inicio,Fin,Duracion,Progreso\n";
        (localTasks || []).forEach((t: any) => {
            csvContent += `"${t.id}","${t.name}","${t.start || ''}","${t.end || ''}",${t.duration || 0},${(t.progress || 0).toFixed(2)}%\n`;
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Datos_Reporte_Obra_${config.name || 'Local'}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSaveLocalReport = async () => {
        if (!reportTitle.trim()) {
            alert("Por favor, ingrese un nombre para el reporte.");
            return;
        }
        setSavingLocal(true);
        try {
            const powerbiReportId = reportId && reportId.startsWith('local-') 
                ? reportId 
                : `local-${Math.random().toString(36).substring(2, 11)}-${Date.now().toString(36)}`;
            
            await registerPowerBiReport(
                powerbiReportId,
                reportTitle,
                reportScope,
                [activeProjectId],
                {
                    category: reportCategory,
                    description: reportDescription || 'Reporte interactivo local.',
                    components: selectedComponents
                }
            );

            if (onReportSaved) {
                onReportSaved(powerbiReportId, reportTitle);
            }
        } catch (err: any) {
            console.error(err);
            alert(`Error al guardar reporte local: ${err.message || 'Error del servidor'}`);
        } finally {
            setSavingLocal(false);
        }
    };

    const toggleComponent = (comp: string) => {
        setSelectedComponents(prev => 
            prev.includes(comp) ? prev.filter(c => c !== comp) : [...prev, comp]
        );
    };

    const hasAccess = isAdmin || canView || canDesign;
    if (!hasAccess) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm min-h-[500px]">
                <div className="size-16 rounded-2xl bg-red-500/10 text-red-500 border border-red-500/20 flex items-center justify-center mx-auto mb-4">
                    <span className="material-symbols-outlined text-[36px]">security</span>
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Acceso Denegado</h3>
                <p className="text-xs text-slate-600 dark:text-slate-400 max-w-sm mx-auto">
                    No cuenta con los permisos necesarios para visualizar o diseñar reportes. Contacte a un administrador.
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full w-full bg-slate-50/90 dark:bg-slate-900/90 rounded-2xl overflow-hidden shadow-sm transition-all glow-blue">
            {config.isMocked && (
                <style dangerouslySetInnerHTML={{ __html: `
                    @media print {
                        body * {
                            visibility: hidden;
                        }
                        #print-local-report-content, #print-local-report-content * {
                            visibility: visible;
                        }
                        #print-local-report-content {
                            position: absolute;
                            left: 0;
                            top: 0;
                            width: 100% !important;
                            height: auto !important;
                            background: white !important;
                            color: black !important;
                        }
                        .recharts-responsive-container {
                            width: 100% !important;
                            height: 320px !important;
                            display: block !important;
                        }
                        .recharts-wrapper {
                            width: 100% !important;
                            height: 320px !important;
                        }
                        svg {
                            width: 100% !important;
                            height: 100% !important;
                            display: block !important;
                        }
                        .no-print {
                            display: none !important;
                        }
                    }
                ` }} />
            )}

            {/* Header Control Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 no-print">
                <div className="flex items-center gap-3">
                    <div className="size-9 rounded-xl bg-gradient-to-tr from-amber-500 to-yellow-400 flex items-center justify-center text-white shadow-md shadow-amber-500/20">
                        <span className="material-symbols-outlined text-[20px]">analytics</span>
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-base font-bold text-slate-900 dark:text-white">
                                {config.isMocked ? 'Visor Analítico Local' : 'Power BI Embedded'}
                            </h2>
                            <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                Capa SQL bi Activa
                            </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            {config.isMocked 
                                ? `Reporte de Obra: ${config.name || 'Personalizado'}`
                                : 'Motor Analítico Incrustado K-Lyapp (Piloto PPC - Last Planner System)'}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* View/Edit Mode Switcher */}
                    {(isAdmin || canDesign || canModifyDesign) && (
                        <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-xl border border-slate-200 dark:border-slate-700">
                            <button
                                onClick={() => setMode('view')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${mode === 'view'
                                        ? 'bg-white dark:bg-slate-800 text-primary shadow-sm'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-[16px]">visibility</span>
                                Lectura
                            </button>
                            <button
                                onClick={() => setMode('edit')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${mode === 'edit'
                                        ? 'bg-white dark:bg-slate-800 text-amber-600 dark:text-amber-400 shadow-sm'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-[16px]">edit</span>
                                Edición
                            </button>
                        </div>
                    )}

                    <button
                        onClick={() => setShowDiagPanel(!showDiagPanel)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${showDiagPanel
                                ? 'bg-primary text-white border-primary shadow-md'
                                : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                            }`}
                        title="Ver diagnóstico de conexión y variables"
                    >
                        <span className="material-symbols-outlined text-[16px]">settings_ethernet</span>
                        Diagnóstico BD
                    </button>
                </div>
            </div>

            {/* Diagnostic Drawer */}
            {showDiagPanel && (
                <div className="bg-slate-100 dark:bg-slate-950/80 border-b border-slate-200 dark:border-slate-800 p-5 space-y-4 animate-fade-in no-print">
                    {config.status && (
                        <div className="px-4 py-2.5 rounded-xl text-xs bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-900 flex items-center gap-2">
                            <span className="material-symbols-outlined text-[18px]">info</span>
                            <span><strong>Estado de la API:</strong> {config.status}</span>
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                        <div className="bg-white dark:bg-slate-900 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                            <span className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Esquema Analítico SQL</span>
                            <p className="text-emerald-600 dark:text-emerald-400 font-mono font-medium">{status.sqlSchemaStatus}</p>
                            <p className="text-[11px] text-slate-500 mt-1">7 vistas analíticas + Rol NOLOGIN bi_reader</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                            <span className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Métricas PPC Reconciliadas</span>
                            <p className="text-primary font-bold">PPC_VALID: {ppcMetrics.ppcValid.toFixed(2)}%</p>
                            <p className="text-slate-500 dark:text-slate-400">PPC_GROSS: {ppcMetrics.ppcGross.toFixed(2)}%</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                            <span className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Parámetros Reporte</span>
                            <p className="text-slate-600 dark:text-slate-400 font-mono text-[11px]">
                                ID: {config.reportId || reportId || 'No cargado'}
                            </p>
                            <p className="text-slate-600 dark:text-slate-400 font-mono text-[11px]">
                                Modo local interactivo: {config.isMocked ? 'Sí (React/Recharts)' : 'No (Power BI Cloud)'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Render Canvas */}
            <div className="flex-1 relative min-h-[600px] bg-slate-100 dark:bg-slate-950 flex flex-col">
                {loading ? (
                    <div className="flex flex-col items-center justify-center flex-1 gap-3 p-8">
                        <div className="size-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-xs font-medium text-slate-600 dark:text-slate-400">
                            Cargando componentes analíticos...
                        </p>
                    </div>
                ) : error ? (
                    <div className="max-w-xl text-center p-8 space-y-5 animate-fade-in mx-auto mt-16">
                        <div className="size-16 rounded-2xl bg-red-500/10 text-red-500 border border-red-500/20 flex items-center justify-center mx-auto shadow-inner">
                            <span className="material-symbols-outlined text-[36px]">report_problem</span>
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                Error de Conexión Analítica
                            </h3>
                            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed max-w-sm mx-auto">
                                {error.message}
                            </p>
                        </div>

                        <div className="bg-slate-900 text-slate-200 text-left p-4 rounded-xl font-mono text-[11px] space-y-1 shadow-inner border border-slate-800 max-w-sm mx-auto">
                            <p><span className="text-red-400">Código:</span> {error.code}</p>
                            {error.requestId && <p><span className="text-amber-400">Solicitud ID:</span> {error.requestId}</p>}
                        </div>
                    </div>
                ) : showPermissionError ? (
                    <div className="max-w-xl text-center p-8 space-y-4 animate-fade-in mx-auto mt-16">
                        <div className="size-16 rounded-2xl bg-red-500/10 text-red-500 border border-red-500/20 flex items-center justify-center mx-auto shadow-inner">
                            <span className="material-symbols-outlined text-[36px]">security</span>
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                Modo Edición No Autorizado por el Servidor
                            </h3>
                            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                La sesión no cuenta con privilegios de escritura de Power BI.
                            </p>
                        </div>
                    </div>
                ) : config.isMocked && (action === 'create' || mode === 'edit') ? (
                    // ==========================================
                    // LOCAL DESIGNER / BUILDER CANVAS
                    // ==========================================
                    <div className="flex flex-1 overflow-hidden h-full">
                        {/* Left sidebar: Components Checklist */}
                        <div className="w-64 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 p-5 space-y-5 flex flex-col justify-between">
                            <div className="space-y-4">
                                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Componentes de Reporte</h3>
                                <div className="space-y-2.5">
                                    {[
                                        { id: 'kpi', label: 'Resumen Ejecutivo (KPIs)', desc: 'Panel superior de tarjetas con métricas consolidadas.' },
                                        { id: 'scurve', label: 'Curva S de Avance', desc: 'Progreso real acumulado vs curva de planificación.' },
                                        { id: 'ppc', label: 'Cumplimiento PPC', desc: 'Porcentaje de Plan Completado por semana.' },
                                        { id: 'quality', label: 'Gestión de Calidad', desc: 'Métricas de estado de protocolos de control.' },
                                        { id: 'subcontracts', label: 'Finanzas Subcontratos', desc: 'Costos contratados vs montos aprobados.' }
                                    ].map(c => (
                                        <label key={c.id} className="flex gap-3 p-3 rounded-xl border border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/40 cursor-pointer transition-all select-none">
                                            <input 
                                                type="checkbox" 
                                                checked={selectedComponents.includes(c.id)} 
                                                onChange={() => toggleComponent(c.id)}
                                                className="mt-0.5 rounded text-primary focus:ring-primary size-4"
                                            />
                                            <div>
                                                <span className="text-xs font-bold text-slate-880 dark:text-slate-200 block">{c.label}</span>
                                                <span className="text-[10px] text-slate-400 leading-normal block mt-0.5">{c.desc}</span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <button
                                onClick={handleSaveLocalReport}
                                disabled={savingLocal}
                                className="w-full py-2.5 px-4 bg-primary text-white text-xs font-bold rounded-xl hover:bg-primary/95 transition-all shadow-md shadow-primary/20 flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                <span className="material-symbols-outlined text-[18px]">save</span>
                                {savingLocal ? 'Guardando...' : 'Guardar Reporte'}
                            </button>
                        </div>

                        {/* Center pane: Canvas Live Preview */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-6 max-h-[calc(100vh-12rem)]">
                            <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-3">
                                <div>
                                    <span className="text-[10px] font-bold text-amber-500 uppercase tracking-widest block mb-0.5">Diseño Activo</span>
                                    <h3 className="text-base font-bold text-slate-900 dark:text-white">{reportTitle || 'Reporte sin nombre'}</h3>
                                </div>
                                <span className="text-xs px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-500 font-medium">Vista Previa de Edición</span>
                            </div>

                            {/* Conditional widgets list preview */}
                            {selectedComponents.includes('kpi') && (
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 opacity-75">
                                    <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center gap-3">
                                        <span className="material-symbols-outlined text-[28px] text-primary">construction</span>
                                        <div>
                                            <span className="text-[10px] text-slate-400 block">Avance Físico</span>
                                            <span className="text-base font-black text-slate-880 dark:text-slate-200">{projectProgress.toFixed(2)}%</span>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center gap-3">
                                        <span className="material-symbols-outlined text-[28px] text-emerald-500">task_alt</span>
                                        <div>
                                            <span className="text-[10px] text-slate-400 block">PPC Válido</span>
                                            <span className="text-base font-black text-slate-880 dark:text-slate-200">{ppcMetrics.ppcValid.toFixed(1)}%</span>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center gap-3">
                                        <span className="material-symbols-outlined text-[28px] text-amber-500">payments</span>
                                        <div>
                                            <span className="text-[10px] text-slate-400 block">Visado Subcontratos</span>
                                            <span className="text-base font-black text-slate-880 dark:text-slate-200">{formatCLP(financialMetrics.approved)}</span>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center gap-3">
                                        <span className="material-symbols-outlined text-[28px] text-purple-500">account_balance_wallet</span>
                                        <div>
                                            <span className="text-[10px] text-slate-400 block">Garantía Retenida</span>
                                            <span className="text-base font-black text-slate-880 dark:text-slate-200">{formatCLP(financialMetrics.retentions)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 opacity-75">
                                {selectedComponents.includes('scurve') && (
                                    <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm h-64 flex items-center justify-center text-xs text-slate-400 font-bold">
                                        [Gráfico Curva S Habilitado]
                                    </div>
                                )}
                                {selectedComponents.includes('ppc') && (
                                    <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm h-64 flex items-center justify-center text-xs text-slate-400 font-bold">
                                        [Gráfico Cumplimiento PPC Habilitado]
                                    </div>
                                )}
                                {selectedComponents.includes('quality') && (
                                    <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm h-64 flex items-center justify-center text-xs text-slate-400 font-bold">
                                        [Gráfico Calidad Habilitado]
                                    </div>
                                )}
                                {selectedComponents.includes('subcontracts') && (
                                    <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm h-64 flex items-center justify-center text-xs text-slate-400 font-bold">
                                        [Gráfico Finanzas Habilitado]
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right sidebar: Properties form */}
                        <div className="w-80 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 p-5 space-y-4">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Propiedades del Reporte</h3>
                            
                            <div className="space-y-3">
                                <div>
                                    <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">Nombre del Reporte</label>
                                    <input 
                                        type="text" 
                                        value={reportTitle} 
                                        onChange={e => setReportTitle(e.target.value)} 
                                        className="w-full text-xs p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 focus:outline-none focus:border-primary text-slate-800 dark:text-white"
                                        placeholder="Ej: Reporte de Avance Semanal"
                                    />
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">Alcance (Filtro RLS)</label>
                                    <select 
                                        value={reportScope} 
                                        onChange={e => setReportScope(e.target.value as any)}
                                        className="w-full text-xs p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 focus:outline-none focus:border-primary text-slate-800 dark:text-white cursor-pointer"
                                    >
                                        <option value="obra">Obra (Solo este proyecto)</option>
                                        <option value="multiobra">Multiobra (Proyectos asignados)</option>
                                        <option value="corporativo">Corporativo (Todos los proyectos)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">Categoría</label>
                                    <select 
                                        value={reportCategory} 
                                        onChange={e => setReportCategory(e.target.value)}
                                        className="w-full text-xs p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 focus:outline-none focus:border-primary text-slate-800 dark:text-white cursor-pointer"
                                    >
                                        <option value="Planificación">Planificación</option>
                                        <option value="Calidad">Calidad</option>
                                        <option value="Subcontratos">Subcontratos</option>
                                        <option value="SSOMA">SSOMA</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-slate-600 dark:text-slate-400 block mb-1">Descripción</label>
                                    <textarea 
                                        value={reportDescription} 
                                        onChange={e => setReportDescription(e.target.value)}
                                        rows={3}
                                        className="w-full text-xs p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 focus:outline-none focus:border-primary text-slate-800 dark:text-white resize-none"
                                        placeholder="Descripción breve del contenido analítico..."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : config.isMocked && mode === 'view' ? (
                    // ==========================================
                    // LOCAL INTERACTIVE VIEW-ONLY VISOR
                    // ==========================================
                    <div id="print-local-report-content" className="flex flex-col flex-1 p-6 space-y-6 overflow-y-auto max-h-[calc(100vh-10rem)]">
                        
                        {/* Interactive Page/Tab Selector and Export dropdown inside Canvas */}
                        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-700 pb-3 no-print">
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => setLocalActiveTab('resumen')}
                                    className={`px-4 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 ${localActiveTab === 'resumen' 
                                        ? 'bg-primary text-white shadow-md shadow-primary/20' 
                                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/60'}`}
                                >
                                    <span className="material-symbols-outlined text-[16px]">dashboard</span>
                                    Resumen Ejecutivo
                                </button>
                                {selectedComponents.includes('scurve') && (
                                    <button
                                        onClick={() => setLocalActiveTab('scurve')}
                                        className={`px-4 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 ${localActiveTab === 'scurve' 
                                            ? 'bg-primary text-white shadow-md shadow-primary/20' 
                                            : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/60'}`}
                                    >
                                        <span className="material-symbols-outlined text-[16px]">show_chart</span>
                                        Curva S Avance
                                    </button>
                                )}
                                {selectedComponents.includes('ppc') && (
                                    <button
                                        onClick={() => setLocalActiveTab('ppc')}
                                        className={`px-4 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 ${localActiveTab === 'ppc' 
                                            ? 'bg-primary text-white shadow-md shadow-primary/20' 
                                            : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/60'}`}
                                    >
                                        <span className="material-symbols-outlined text-[16px]">playlist_add_check</span>
                                        Cumplimiento PPC
                                    </button>
                                )}
                                {selectedComponents.includes('quality') && (
                                    <button
                                        onClick={() => setLocalActiveTab('quality')}
                                        className={`px-4 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 ${localActiveTab === 'quality' 
                                            ? 'bg-primary text-white shadow-md shadow-primary/20' 
                                            : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/60'}`}
                                    >
                                        <span className="material-symbols-outlined text-[16px]">gpp_good</span>
                                        Gestión Calidad
                                    </button>
                                )}
                                {selectedComponents.includes('subcontracts') && (
                                    <button
                                        onClick={() => setLocalActiveTab('subcontracts')}
                                        className={`px-4 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 ${localActiveTab === 'subcontracts' 
                                            ? 'bg-primary text-white shadow-md shadow-primary/20' 
                                            : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/60'}`}
                                    >
                                        <span className="material-symbols-outlined text-[16px]">receipt_long</span>
                                        Subcontratos Costos
                                    </button>
                                )}
                            </div>

                            {/* Export dropdown */}
                            <div className="relative group">
                                <button className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all select-none">
                                    <span className="material-symbols-outlined text-[18px]">download</span>
                                    Exportar Reporte
                                    <span className="material-symbols-outlined text-[16px]">keyboard_arrow_down</span>
                                </button>
                                <div className="absolute right-0 mt-1.5 w-44 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 py-1 font-sans">
                                    <button onClick={handleExportPDF} className="w-full text-left px-4 py-2.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[16px] text-rose-500">picture_as_pdf</span>
                                        Exportar a PDF
                                    </button>
                                    <button onClick={handleExportHTML} className="w-full text-left px-4 py-2.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[16px] text-blue-500">html</span>
                                        Descargar HTML
                                    </button>
                                    <button onClick={handleExportCSV} className="w-full text-left px-4 py-2.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[16px] text-emerald-500">table_rows</span>
                                        Descargar CSV (Datos)
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Render Tab Contents */}
                        {localActiveTab === 'resumen' && (
                            <div className="space-y-6 animate-fade-in">
                                {/* Top KPI Row */}
                                {selectedComponents.includes('kpi') && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                                        <div className="glass-card p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:scale-[1.02] transition-all duration-300">
                                            <div className="size-12 rounded-xl bg-blue-500/10 text-primary flex items-center justify-center">
                                                <span className="material-symbols-outlined text-[30px]">construction</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Avance Físico</span>
                                                <span className="text-xl font-black text-slate-850 dark:text-slate-100">{projectProgress.toFixed(2)}%</span>
                                                <span className="text-[9px] text-slate-400 block mt-0.5">Global Ponderado</span>
                                            </div>
                                        </div>

                                        <div className="glass-card p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:scale-[1.02] transition-all duration-300">
                                            <div className="size-12 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                                                <span className="material-symbols-outlined text-[30px]">task_alt</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">PPC Válido</span>
                                                <span className="text-xl font-black text-slate-850 dark:text-slate-100">{ppcMetrics.ppcValid.toFixed(1)}%</span>
                                                <span className="text-[9px] text-emerald-500 block mt-0.5">Last Planner Standard</span>
                                            </div>
                                        </div>

                                        <div className="glass-card p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:scale-[1.02] transition-all duration-300">
                                            <div className="size-12 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                                                <span className="material-symbols-outlined text-[30px]">payments</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Pagos Aprobados</span>
                                                <span className="text-xl font-black text-slate-850 dark:text-slate-100">{formatCLP(financialMetrics.approved)}</span>
                                                <span className="text-[9px] text-slate-400 block mt-0.5">Neto Subcontratos</span>
                                            </div>
                                        </div>

                                        <div className="glass-card p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:scale-[1.02] transition-all duration-300">
                                            <div className="size-12 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center">
                                                <span className="material-symbols-outlined text-[30px]">account_balance_wallet</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Garantía Retenida</span>
                                                <span className="text-xl font-black text-slate-850 dark:text-slate-100">{formatCLP(financialMetrics.retentions)}</span>
                                                <span className="text-[9px] text-slate-400 block mt-0.5">Retención Fija 5%</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Main Charts Grid inside Resumen tab */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {selectedComponents.includes('scurve') && scurvePoints.length > 0 && (
                                        <div className="glass-card p-5 rounded-2xl shadow-sm">
                                            <h4 className="text-xs font-bold text-slate-850 dark:text-slate-150 uppercase tracking-wider mb-4">Curva S de Avance de Obra</h4>
                                            <div className="h-60 w-full">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <AreaChart data={scurvePoints}>
                                                        <defs>
                                                            <linearGradient id="colorPlan" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                                                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                                            </linearGradient>
                                                            <linearGradient id="colorReal" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                                                                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                        <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#888888" />
                                                        <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} stroke="#888888" />
                                                        <Tooltip />
                                                        <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
                                                        <Area name="Progreso Programado" type="monotone" dataKey="plan" stroke="#3b82f6" fillOpacity={1} fill="url(#colorPlan)" strokeWidth={2} />
                                                        <Area name="Progreso Real" type="monotone" dataKey="real" stroke="#10b981" fillOpacity={1} fill="url(#colorReal)" strokeWidth={2} />
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    )}

                                    {selectedComponents.includes('ppc') && ppcTrendData.length > 0 && (
                                        <div className="glass-card p-5 rounded-2xl shadow-sm">
                                            <h4 className="text-xs font-bold text-slate-850 dark:text-slate-150 uppercase tracking-wider mb-4">Cumplimiento de Planificación (PPC)</h4>
                                            <div className="h-60 w-full">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={ppcTrendData}>
                                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                        <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#888888" />
                                                        <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} stroke="#888888" />
                                                        <Tooltip />
                                                        <Legend verticalAlign="top" height={36} iconType="rect" wrapperStyle={{ fontSize: '11px' }} />
                                                        <Bar name="Porcentaje PPC" dataKey="PPC" radius={[4, 4, 0, 0]}>
                                                            {ppcTrendData.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.PPC >= 85 ? '#10b981' : '#f59e0b'} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {localActiveTab === 'scurve' && (
                            <div className="glass-card p-6 rounded-2xl shadow-sm space-y-4 animate-fade-in">
                                <div>
                                    <h3 className="text-sm font-bold text-slate-850 dark:text-slate-100">Curva S e Históricos de Obra</h3>
                                    <p className="text-xs text-slate-500">Mapeo del avance real físico de obra contra la línea de planificación de contrato.</p>
                                </div>
                                <div className="h-96 w-full">
                                    {scurvePoints.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={scurvePoints}>
                                                <defs>
                                                    <linearGradient id="colorPlanTab" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                                    </linearGradient>
                                                    <linearGradient id="colorRealTab" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#888888" />
                                                <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} stroke="#888888" />
                                                <Tooltip />
                                                <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                                                <Area name="Progreso Programado Contractual" type="monotone" dataKey="plan" stroke="#3b82f6" fillOpacity={1} fill="url(#colorPlanTab)" strokeWidth={3} />
                                                <Area name="Progreso Real Físico" type="monotone" dataKey="real" stroke="#10b981" fillOpacity={1} fill="url(#colorRealTab)" strokeWidth={3} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-xs text-slate-400">No hay tareas programadas.</div>
                                    )}
                                </div>
                            </div>
                        )}

                        {localActiveTab === 'ppc' && (
                            <div className="glass-card p-6 rounded-2xl shadow-sm space-y-6 animate-fade-in">
                                <div>
                                    <h3 className="text-sm font-bold text-slate-850 dark:text-slate-100">Evaluación de Porcentaje de Plan Completado (PPC)</h3>
                                    <p className="text-xs text-slate-500">Métricas analíticas del Last Planner System basadas en el cumplimiento semanal de compromisos.</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                    <div className="glass-card p-4 rounded-xl shadow-sm">
                                        <span className="text-[10px] font-bold text-slate-400 block uppercase">PPC Promedio Válido</span>
                                        <span className="text-2xl font-black text-primary block mt-1">{ppcMetrics.ppcValid.toFixed(2)}%</span>
                                        <span className="text-[9px] text-slate-400 block mt-1">Excluye asignaciones pendientes de evaluación</span>
                                    </div>
                                    <div className="glass-card p-4 rounded-xl shadow-sm">
                                        <span className="text-[10px] font-bold text-slate-400 block uppercase">PPC Bruto Auxiliar</span>
                                        <span className="text-2xl font-black text-slate-850 dark:text-slate-200 block mt-1">{ppcMetrics.ppcGross.toFixed(2)}%</span>
                                        <span className="text-[9px] text-slate-400 block mt-1">Calculado con respecto al total de asignaciones</span>
                                    </div>
                                    <div className="glass-card p-4 rounded-xl shadow-sm">
                                        <span className="text-[10px] font-bold text-slate-400 block uppercase">Asignaciones de Obra</span>
                                        <span className="text-2xl font-black text-emerald-500 block mt-1">{ppcMetrics.executed} / {ppcMetrics.executed + ppcMetrics.notExecuted}</span>
                                        <span className="text-[9px] text-emerald-500 block mt-1">Asignaciones semanales ejecutadas con éxito</span>
                                    </div>
                                </div>

                                <div className="h-80 w-full">
                                    {ppcTrendData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={ppcTrendData}>
                                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#888888" />
                                                <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} stroke="#888888" />
                                                <Tooltip />
                                                <Bar name="Porcentaje PPC" dataKey="PPC" radius={[4, 4, 0, 0]}>
                                                    {ppcTrendData.map((entry, index) => (
                                                        <Cell key={`cell-tab-${index}`} fill={entry.PPC >= 85 ? '#10b981' : '#f59e0b'} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-xs text-slate-400">No hay planes semanales cerrados.</div>
                                    )}
                                </div>
                            </div>
                        )}

                        {localActiveTab === 'quality' && (
                            <div className="glass-card p-6 rounded-2xl shadow-sm space-y-6 animate-fade-in">
                                <div>
                                    <h3 className="text-sm font-bold text-slate-850 dark:text-slate-100">Estado de Protocolos de Calidad</h3>
                                    <p className="text-xs text-slate-500">Métricas analíticas sobre aprobaciones y rechazos de calidad en terreno.</p>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
                                    <div className="h-56 flex items-center justify-center">
                                        {qualityMetrics.total > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={[
                                                            { name: 'Cerrados', value: qualityMetrics.closedCount, color: '#10b981' },
                                                            { name: 'Abiertos', value: qualityMetrics.openCount, color: '#3b82f6' },
                                                            { name: 'Rechazados', value: qualityMetrics.rejectedCount, color: '#f43f5e' }
                                                        ]}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={60}
                                                        outerRadius={80}
                                                        paddingAngle={5}
                                                        dataKey="value"
                                                    >
                                                        {[
                                                            { color: '#10b981' },
                                                            { color: '#3b82f6' },
                                                            { color: '#f43f5e' }
                                                        ].map((entry, index) => (
                                                            <Cell key={`cell-pie-${index}`} fill={entry.color} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="text-xs text-slate-400">No hay protocolos guardados.</div>
                                        )}
                                    </div>

                                    <div className="lg:col-span-2 space-y-4">
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Detalle de Estados</h4>
                                        <div className="space-y-3">
                                            {[
                                                { label: 'Protocolos Cerrados', count: qualityMetrics.closedCount, color: 'bg-emerald-500', pct: qualityMetrics.total > 0 ? (qualityMetrics.closedCount / qualityMetrics.total) * 100 : 0 },
                                                { label: 'Protocolos En Proceso / Abiertos', count: qualityMetrics.openCount, color: 'bg-blue-500', pct: qualityMetrics.total > 0 ? (qualityMetrics.openCount / qualityMetrics.total) * 100 : 0 },
                                                { label: 'Protocolos Rechazados / Con Observaciones', count: qualityMetrics.rejectedCount, color: 'bg-rose-500', pct: qualityMetrics.total > 0 ? (qualityMetrics.rejectedCount / qualityMetrics.total) * 100 : 0 }
                                            ].map((item, idx) => (
                                                <div key={idx} className="space-y-1">
                                                    <div className="flex justify-between text-xs font-medium">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`size-3 rounded-full ${item.color}`} />
                                                            <span className="text-slate-700 dark:text-slate-300">{item.label}</span>
                                                        </div>
                                                        <span className="text-slate-900 dark:text-white font-bold">{item.count} ({item.pct.toFixed(1)}%)</span>
                                                    </div>
                                                    <div className="w-full bg-slate-100 dark:bg-slate-700 h-2 rounded-full overflow-hidden">
                                                        <div className={`h-full ${item.color}`} style={{ width: `${item.pct}%` }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {localActiveTab === 'subcontracts' && (
                            <div className="glass-card p-6 rounded-2xl shadow-sm space-y-6 animate-fade-in">
                                <div>
                                    <h3 className="text-sm font-bold text-slate-850 dark:text-slate-100">Análisis Financiero de Subcontratos</h3>
                                    <p className="text-xs text-slate-500">Comparativa analítica entre montos totales contratados y estados de pago aprobados/liquidados por contratista.</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="h-80">
                                        {subcontractChartData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={subcontractChartData}>
                                                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                    <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#888888" />
                                                    <YAxis tick={{ fontSize: 9 }} stroke="#888888" />
                                                    <Tooltip formatter={(value) => formatCLP(value as number)} />
                                                    <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '11px' }} />
                                                    <Bar name="Monto Contratado" dataKey="Contratado" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                                                    <Bar name="Monto Aprobado/Pagado" dataKey="Visado" fill="#10b981" radius={[3, 3, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="h-full flex items-center justify-center text-xs text-slate-400">No hay contratos registrados.</div>
                                        )}
                                    </div>

                                    <div className="space-y-4">
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Consolidado Financiero</h4>
                                        <div className="divide-y divide-slate-100 dark:divide-slate-700">
                                            {[
                                                { label: 'Total Contratado', val: financialMetrics.contracted, color: 'text-slate-850 dark:text-slate-150' },
                                                { label: 'Total Pagado Visado', val: financialMetrics.approved, color: 'text-emerald-600 dark:text-emerald-400' },
                                                { label: 'Retenciones Acumuladas (Garantías)', val: financialMetrics.retentions, color: 'text-purple-600 dark:text-purple-400' },
                                                { label: 'Saldo Pendiente por Liquidar', val: Math.max(0, financialMetrics.contracted - financialMetrics.approved), color: 'text-blue-600 dark:text-blue-400 font-bold' }
                                            ].map((item, idx) => (
                                                <div key={idx} className="flex justify-between py-3 text-xs">
                                                    <span className="text-slate-500">{item.label}</span>
                                                    <span className={`${item.color} font-black`}>{formatCLP(item.val)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                ) : config.accessToken ? (
                    <>
                        {hasWarningBanner && (
                            <div className="w-full bg-amber-500/10 text-amber-500 border-b border-amber-500/20 px-6 py-2 flex items-center gap-2 z-20 absolute top-0 left-0 right-0 h-10 select-none">
                                <span className="material-symbols-outlined text-[16px]">lock</span>
                                Diseñador en modo Solo Lectura: No tiene permisos de modificación.
                            </div>
                        )}
                        <div
                            ref={embedContainerRef}
                            className={`w-full h-full absolute ${hasWarningBanner ? 'top-10' : 'inset-0'} rounded-b-2xl`}
                            style={{ height: hasWarningBanner ? 'calc(100% - 2.5rem)' : '100%' }}
                        />
                    </>
                ) : (
                    <div className="max-w-xl text-center p-8 space-y-5 animate-fade-in mx-auto mt-16">
                        <div className="size-16 rounded-2xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center mx-auto shadow-inner">
                            <span className="material-symbols-outlined text-[36px]">analytics</span>
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                Módulo de Inteligencia de Negocios Activo
                            </h3>
                            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                La plataforma analítica de K-Lyapp está completamente lista. Seleccione un reporte personalizado en el catálogo superior o use el diseñador para crear visualizaciones interactivas de avance y PPC.
                            </p>
                        </div>

                        <div className="bg-slate-900 text-slate-200 text-left p-4 rounded-xl font-mono text-[11px] space-y-1 shadow-inner border border-slate-800">
                            <p className="text-slate-500"># Infraestructura Analítica Activa</p>
                            <p><span className="text-emerald-400">Esquema SQL:</span> bi (7 vistas analíticas)</p>
                            <p><span className="text-emerald-400">Optimización:</span> Vista materializada de Curva S activa</p>
                            <p><span className="text-emerald-400">Gobernanza:</span> Dataset "K-Lyapp" y RLS por Obra habilitada</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
